import { cn } from '@/shared/lib/utils';
import type { TermBarModel } from '../model/rowLayout';
import { TransportChip } from './TransportChip';

interface TermBarProps {
  bar: TermBarModel;
  /** Pulz rámečku po skoku na termín ze stromu. */
  isHighlighted: boolean;
  onOpen: (termId: string) => void;
}

const TEAR_CLASS = (openStart: boolean, openEnd: boolean): string | undefined => {
  if (openStart && openEnd) return 'phx-cal-tear-both';
  if (openStart) return 'phx-cal-tear-start';
  if (openEnd) return 'phx-cal-tear-end';
  return undefined;
};

/**
 * Pruh jednoho termínu v řadě dnů. Sám o sobě je to jen podklad s názvem — boxy dopravy leží
 * **vedle něj** v téže vrstvě, protože běžně přesahují mimo dny termínu (svoz den předem,
 * návrat po půlnoci). Proto je pruh i jeho boxy jedna „řádka“ (`pointer-events` až na potomcích),
 * kterou nadřazená řada usadila na `top`.
 *
 * Zvýraznění pruhu i s jeho boxy řeší **čistě CSS** (`.phx-cal-bar-wrap:hover` v `calendar.css`).
 * Obal je sice `pointer-events: none`, ale `:hover` na něj sedí i tehdy, když myš míří na některého
 * z jeho potomků — pruh a jeho boxy se tak zvýrazní společně bez jediného renderu. Držet hover ve
 * stavu Reactu by znamenalo překreslit celou plochu kalendáře při každém přejetí myší.
 *
 * Pokračování do sousedního týdne se kreslí jako roztržená (cik-cak) hrana: maskou na podkladu
 * a `polyline` obrysem přes ni.
 */
export function TermBar({ bar, isHighlighted, onOpen }: TermBarProps) {
  return (
    <div
      className="phx-cal-bar-wrap pointer-events-none absolute inset-x-0"
      style={{ top: bar.top, height: bar.height }}
    >
      <button
        type="button"
        onClick={() => onOpen(bar.termId)}
        title={bar.title}
        data-testid="calendar-term-bar"
        data-term-id={bar.termId}
        className={cn(
          'phx-cal-bar-btn pointer-events-auto absolute top-0 bottom-0 cursor-pointer text-left',
          isHighlighted && 'animate-term-highlight rounded-phx-md'
        )}
        style={{ left: bar.left, width: bar.width }}
      >
        <span
          className={cn(
            'phx-cal-bar duration-phx-fast ease-phx-out absolute inset-0 box-border block border-y border-solid transition-[background,border-color]',
            TEAR_CLASS(bar.openStart, bar.openEnd)
          )}
          style={{
            borderLeftWidth: bar.openStart ? 0 : 3,
            borderRightWidth: bar.openEnd ? 0 : 1,
            borderRadius: bar.radius,
          }}
        />

        {bar.openStart && (
          <svg
            width={7}
            height={bar.height}
            viewBox={bar.tearViewBox}
            preserveAspectRatio="none"
            aria-hidden="true"
            className="phx-cal-tear-stroke absolute top-0 left-0 fill-none [stroke-width:1]"
          >
            <polyline points={bar.tearPointsStart} />
          </svg>
        )}
        {bar.openEnd && (
          <svg
            width={7}
            height={bar.height}
            viewBox={bar.tearViewBox}
            preserveAspectRatio="none"
            aria-hidden="true"
            className="phx-cal-tear-stroke absolute top-0 right-0 fill-none [stroke-width:1]"
          >
            <polyline points={bar.tearPointsEnd} />
          </svg>
        )}

        <span
          className="text-phx-text text-phx-sm absolute box-border flex min-w-0 items-center justify-center"
          style={{
            top: bar.titleTop,
            height: bar.titleHeight,
            left: bar.nameLeft,
            width: bar.nameWidth,
            padding: bar.namePadding,
          }}
        >
          <span className="min-w-0 shrink overflow-hidden font-semibold text-ellipsis whitespace-nowrap">
            {bar.label}
          </span>
          <span className="text-phx-text-muted ml-phx-3 shrink-0 tabular-nums">
            {bar.nightsLabel}
          </span>
        </span>
      </button>

      {bar.zones.map((zone) => (
        <div
          key={zone.key}
          className="phx-cal-bar-zone pointer-events-auto absolute flex flex-col gap-0.5"
          style={{ top: zone.top, left: zone.left, right: zone.right, alignItems: zone.align }}
        >
          {zone.items.map((leg) => (
            <TransportChip key={leg.key} leg={leg} />
          ))}
        </div>
      ))}
    </div>
  );
}
